import { getHeaders } from 'src/api/headers'
import { ins } from 'src/api/axios'
import { getError } from 'src/api/utils'
import {
    type BillingProfile,
    type BillingProfileResponse,
    type Contract,
    type GetBillingProfilesResponse,
    type GetContractsResponse,
    type GetRiderPaymentDetailsResponse,
    type RiderPaymentDetails,
} from 'src/api/fm/payments/payments.model'

export const getRiderContracts = async (riderId: string): Promise<Contract[] | null | Error> => {
    const link = encodeURI(`/v1/fm/payments/users/${riderId}/contracts`)
    const headers = await getHeaders()

    return ins
        .get<GetContractsResponse>(link, { headers })
        .then(res => mapGetContracts(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/pricing%20gateway/getPaymentDetails API documentation}
 */
export const getRiderPaymentDetails = async (riderId: string): Promise<RiderPaymentDetails | null | Error> => {
    const link = encodeURI(`/v1/fm/payments/users/${riderId}/payment-details`)
    const headers = await getHeaders()

    return ins
        .get<GetRiderPaymentDetailsResponse>(link, { headers })
        .then(res => mapGetRiderPaymentDetails(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/payments%20gateway/abandonContract API documentation}
 */
export const abandonContract = async (riderId: string, contractId: string): Promise<void | Error> => {
    const link = encodeURI(`/v1/fm/payments/users/${riderId}/contracts/${contractId}/abandon`)
    const headers = await getHeaders()

    return ins
        .put(link, {}, { headers })
        .then(() => undefined)
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/payments%20gateway/refundContract API documentation}
 */
export const cancelAndRefundContract = async (riderId: string, contractId: string): Promise<void | Error> => {
    const link = encodeURI(`/v1/fm/payments/users/${riderId}/contracts/${contractId}/refund`)
    const headers = await getHeaders()

    return ins
        .put(link, {}, { headers })
        .then(() => undefined)
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/payments%20gateway/stopContractRenewal API documentation}
 */
export const stopContractRenewal = async (riderId: string, contractId: string): Promise<void | Error> => {
    const link = encodeURI(`/v1/fm/payments/users/${riderId}/contracts/${contractId}/stop-renewal`)
    const headers = await getHeaders()

    return ins
        .put(link, {}, { headers })
        .then(() => undefined)
        .catch(e => getError(e, 'response.data.errors[0]'))
}

const mapGetContracts = (response: GetContractsResponse): Contract[] => {
    if (response.data) {
        return response.data.map(
            ({
                contractId,
                name,
                zones,
                minutesUsed,
                rideCount,
                active,
                recurrent,
                cancelled,
                renewsOn,
                expiresAt,
            }) => ({
                contractId,
                name,
                zones,
                minutesUsed,
                rideCount,
                active,
                recurrent,
                cancelled,
                renewsOn,
                expiresAt,
            }),
        )
    }
    return []
}

const mapGetRiderPaymentDetails = (response: GetRiderPaymentDetailsResponse): RiderPaymentDetails | null => {
    if (response.braintreeCustomerIds) {
        return {
            braintreeCustomerIds: response.braintreeCustomerIds,
        }
    }

    return null
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/pricing%20gateway/getUserBillingProfiles API documentation}
 */
export const getRiderBillingProfiles = async (riderId: string): Promise<BillingProfile[] | Error> => {
    const link = encodeURI(`/v1/fm/pricing/users/${riderId}/billing-profiles`)
    const headers = await getHeaders()

    return ins
        .get<GetBillingProfilesResponse>(link, { headers })
        .then(res => (res.data.data ?? []).map(mapBillingProfile))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

const mapBillingProfile = (p: BillingProfileResponse): BillingProfile => ({
    id: p.id,
    userId: p.user_id,
    type: p.type,
    isActive: p.is_active,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    deletedAt: p.deleted_at ?? null,
    companyId: p.company_id ?? '',
    companySlug: p.company_slug ?? '',
    companyName: p.company_name ?? '',
    inviteEmail: p.invite_email ?? '',
})
